import type paper from "paper";
import type { ConstructionLine, Project, Vec2 } from "../Project";
import { projectConstructionLines } from "../Project";
import {
  applyMirror,
  buildMirrorMatrix,
  deriveConstructionMirrors,
  enforceMirrorSiblingInvariant,
  isMirrorDerived,
  pruneMirrorsForDeletedLines,
  resolveAxisSpec,
  type MirrorMatrix,
} from "../constructionMirror";
import { newId } from "../ids";
import { isOffsetDerived } from "../constructionOffset";
import { MIRROR_TOOL_ICON_SVG } from "../../icons/mirrorTool";
import type {
  Connection,
  ConnectionChipSpec,
  ConnectionRowSpec,
} from "./Connection";
import {
  reachableMirrors,
  resolveMirrorRoot,
  detectMirrorCycles,
} from "./mirrorWalkers";

// Re-export the pure walkers so existing barrel imports
// (`from "../model/connections"`) keep finding them at the same
// names — the helpers physically live in `mirrorWalkers.ts` now
// so vitest unit tests can import them without dragging paper-full
// through the SelectTool import chain.
export { reachableMirrors, resolveMirrorRoot, detectMirrorCycles };

/** Issue #180 follow-up: the mirror façade for the Connections
 *  registry. Wraps the existing helpers in
 *  `src/model/constructionMirror.ts` and adds the bidirectional
 *  helpers below so an edit on a mirror-derived line can propagate
 *  back to its source. */

const KIND = "mirror" as const;

/** Mirror is the first 3-participant connection — source + axis +
 *  derived all share the SAME connection key so chip/row hover
 *  cross-highlights light up all three at once. Keyed by the
 *  derived line id (each mirror is uniquely identified by its
 *  derived line). */
function mirrorKey(derivedId: string): string {
  return `mirror:${derivedId}`;
}

export const mirrorConnection: Connection = {
  kind: KIND,
  // Runs last in the commit pipeline so mirror-of-offset reads
  // the offset's just-derived pathData on the same commit.
  order: 2,

  chipsForLine(line, _project, ctx) {
    const chips: ConnectionChipSpec[] = [];
    if (line.mirrorSource) {
      chips.push({
        kind: KIND,
        connectionKey: mirrorKey(line.id),
        role: "derived",
        iconSvg: MIRROR_TOOL_ICON_SVG,
        ariaLabel: "Mirror of source",
        title: "Mirror of source",
        className: "mirror-chip",
        hoverRefs: [
          { kind: "line", constructionId: line.mirrorSource.sourceId },
        ],
        clickTarget: { constructionId: line.mirrorSource.sourceId },
        extraDatasetEntries: [
          ["mirrorPair", mirrorKey(line.id)],
          ["mirrorRole", "derived"],
        ],
      });
    }
    for (const other of ctx.allLines) {
      if (other.mirrorSource?.sourceId === line.id) {
        chips.push({
          kind: KIND,
          connectionKey: mirrorKey(other.id),
          role: "source",
          iconSvg: MIRROR_TOOL_ICON_SVG,
          ariaLabel: "Has mirror",
          title: "Has mirror",
          className: "mirror-chip",
          hoverRefs: [{ kind: "line", constructionId: other.id }],
          clickTarget: { constructionId: other.id },
          extraDatasetEntries: [
            ["mirrorPair", mirrorKey(other.id)],
            ["mirrorRole", "source"],
          ],
        });
      }
      if (other.mirrorSource?.axis.constructionId === line.id) {
        chips.push({
          kind: KIND,
          connectionKey: mirrorKey(other.id),
          role: "axis",
          iconSvg: MIRROR_TOOL_ICON_SVG,
          ariaLabel: "Used as mirror axis",
          title: "Used as mirror axis",
          className: "mirror-chip",
          hoverRefs: [{ kind: "line", constructionId: other.id }],
          clickTarget: { constructionId: other.id },
          extraDatasetEntries: [
            ["mirrorPair", mirrorKey(other.id)],
            ["mirrorRole", "axis"],
          ],
        });
      }
    }
    return chips;
  },

  rowsForLine(line, _project, ctx) {
    const rows: ConnectionRowSpec[] = [];
    const detachCommit =
      (derivedId: string) =>
      (editor: import("../../tools/EditorContext").EditorContext) => {
        editor.commit("Detach mirror", (proj) => ({
          ...proj,
          constructionLines: (proj.constructionLines ?? []).map((l) => {
            if (l.id !== derivedId) return l;
            const { mirrorSource: _drop, ...rest } = l;
            return rest;
          }),
        }));
      };
    if (line.mirrorSource) {
      const src = ctx.allLines.find(
        (l) => l.id === line.mirrorSource!.sourceId,
      );
      if (src) {
        const partnerName =
          src.name ?? `Construction ${ctx.allLines.indexOf(src) + 1}`;
        rows.push({
          kind: KIND,
          connectionKey: mirrorKey(line.id),
          role: "derived",
          iconSvg: MIRROR_TOOL_ICON_SVG,
          ariaLabel: `Mirror of ${partnerName}`,
          title: `Mirror of ${partnerName}`,
          className: "mirror-chip",
          hoverRefs: [{ kind: "line", constructionId: src.id }],
          clickTarget: { constructionId: src.id },
          extraDatasetEntries: [
            ["mirrorPair", mirrorKey(line.id)],
            ["mirrorRole", "derived"],
          ],
          label: `Mirror of ${partnerName}`,
          onDetach: detachCommit(line.id),
        });
      }
    }
    for (const other of ctx.allLines) {
      if (other.mirrorSource?.sourceId === line.id) {
        const partnerName =
          other.name ?? `Construction ${ctx.allLines.indexOf(other) + 1}`;
        rows.push({
          kind: KIND,
          connectionKey: mirrorKey(other.id),
          role: "source",
          iconSvg: MIRROR_TOOL_ICON_SVG,
          ariaLabel: `Mirror → ${partnerName}`,
          title: `Mirror → ${partnerName}`,
          className: "mirror-chip",
          hoverRefs: [{ kind: "line", constructionId: other.id }],
          clickTarget: { constructionId: other.id },
          extraDatasetEntries: [
            ["mirrorPair", mirrorKey(other.id)],
            ["mirrorRole", "source"],
          ],
          label: `Mirror → ${partnerName}`,
          onDetach: detachCommit(other.id),
        });
      }
      if (other.mirrorSource?.axis.constructionId === line.id) {
        const partnerName =
          other.name ?? `Construction ${ctx.allLines.indexOf(other) + 1}`;
        rows.push({
          kind: KIND,
          connectionKey: mirrorKey(other.id),
          role: "axis",
          iconSvg: MIRROR_TOOL_ICON_SVG,
          ariaLabel: `Axis for ${partnerName}`,
          title: `Axis for ${partnerName}`,
          className: "mirror-chip",
          hoverRefs: [{ kind: "line", constructionId: other.id }],
          clickTarget: { constructionId: other.id },
          extraDatasetEntries: [
            ["mirrorPair", mirrorKey(other.id)],
            ["mirrorRole", "axis"],
          ],
          label: `Axis for ${partnerName}`,
          onDetach: detachCommit(other.id),
        });
      }
    }
    return rows;
  },

  derive({ project, scope }) {
    // Two-pass: enforce the sibling invariant FIRST (auto-create any
    // missing `mirror(O, X)` for every existing `mirror(S, X)` where
    // O is an offset descendant of S — or, recursively, where O is
    // a logical sibling reachable through a mirror-of-mirror chain).
    // Then re-derive all mirror pathData so the newly-seeded
    // siblings get their canonical geometry. Doing the invariant
    // pass FIRST means the re-derive pass naturally covers the
    // newly-created lines without a second loop.
    const withSiblings = enforceMirrorSiblingInvariant(project, scope, () =>
      newId("clin"),
    );
    return deriveConstructionMirrors(withSiblings, scope);
  },

  pruneForDeletedLines({ project, deletedLineIds }) {
    return pruneMirrorsForDeletedLines(project, deletedLineIds);
  },

  rederiveLive({ scene, changedId }) {
    // Delegate to the existing Scene helper — it handles both
    // "the source moved" and "the axis moved" plus mirror-of-mirror
    // recursion.
    scene.rederiveMirrorsAffectedBy(changedId);
  },

  forwardClosure({ project, originIds }) {
    return reachableMirrors(project, originIds);
  },

  resolveRoot(project, lineId) {
    return resolveMirrorRoot(project, lineId);
  },

  isDerived(line) {
    return isMirrorDerived(line);
  },

  detectCycles(project) {
    return detectMirrorCycles(project);
  },

  stripBackRefs(project, lineIds) {
    if (lineIds.size === 0) return project;
    const lines = projectConstructionLines(project);
    let changed = false;
    const next = lines.map((l) => {
      if (!lineIds.has(l.id) || !l.mirrorSource) return l;
      changed = true;
      const { mirrorSource: _drop, ...rest } = l;
      return rest;
    });
    return changed ? { ...project, constructionLines: next } : project;
  },
};

/** Bidirectional mirror editing (issue #180 follow-up): translate
 *  a vertex edit on a mirror-DERIVED line into the equivalent edit
 *  on its non-mirror root source line. Walks the mirror chain to
 *  the root (mirror-of-mirror supported), composes every axis
 *  matrix (matrices commute in this order: M_n · M_{n-1} · … · M_1),
 *  and applies the composed reflection to `targetPos` to get the
 *  source-space position.
 *
 *  Returns `null` (and the caller should refuse the drag) when:
 *   - The chain is broken (an axis line or source line vanished).
 *   - The chain hits an OFFSET layer. Bidirectional editing
 *     through an offset's perpendicular-distance math is deferred
 *     to a follow-up; v1 refuses the drag with "Edit the source
 *     line directly".
 *
 *  The `(pathIndex, segmentIndex)` mapping flows through unchanged —
 *  `mirrorPathData` round-trips through paper.js's
 *  `CompoundPath.transform()`, which preserves segment order on
 *  simple paths. Validated by an e2e fixture. */
export function inverseMirrorVertexEdit(
  project: Project,
  derivedLineId: string,
  pathIndex: number,
  segmentIndex: number,
  targetPos: Vec2,
  scope: paper.PaperScope,
): {
  sourceLineId: string;
  pathIndex: number;
  segmentIndex: number;
  newPos: Vec2;
} | null {
  const lines = projectConstructionLines(project);
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const matrices: MirrorMatrix[] = [];
  let cur = derivedLineId;
  for (let i = 0; i < 1000; i++) {
    const line = byId.get(cur);
    if (!line) return null;
    if (!line.mirrorSource) {
      // Reached a non-mirror line — this is the source root.
      // But if it's offset-derived, refuse: bidirectional editing
      // through offset math is not supported in v1.
      if (isOffsetDerived(line)) return null;
      break;
    }
    const axisSpec = resolveAxisSpec(project, line.mirrorSource.axis, scope);
    if (!axisSpec) return null;
    matrices.push(buildMirrorMatrix(axisSpec));
    cur = line.mirrorSource.sourceId;
  }
  if (cur === derivedLineId) return null;
  // Compose every matrix in the chain. Each matrix is an
  // involution (M·M = I), so applying them in any order
  // round-trips a position from derived-space to source-space —
  // but the chain matters for mirror-of-mirror: the innermost
  // mirror's matrix is applied first.
  let pos: Vec2 = targetPos;
  for (const m of matrices) {
    pos = applyMirror(m, pos);
  }
  return {
    sourceLineId: cur,
    pathIndex,
    segmentIndex,
    newPos: pos,
  };
}

/** Apply the rotational part of a mirror matrix to a DELTA vector
 *  (drops the translation component). Used by whole-line drags on
 *  a mirror-derived line: the source line translates by
 *  `applyMirrorDelta(M, mirrorDelta)`. */
export function applyMirrorDelta(m: MirrorMatrix, delta: Vec2): Vec2 {
  return {
    x: m.m11 * delta.x + m.m12 * delta.y,
    y: m.m21 * delta.x + m.m22 * delta.y,
  };
}

/** Walk the mirror chain starting at `derivedLineId` back to a
 *  non-mirror source root, collecting each axis matrix along the
 *  way (outermost first). Returns `null` when:
 *    - `derivedLineId` is not a mirror,
 *    - any axis line / segment along the chain has vanished, OR
 *    - the chain bottoms out at an offset-derived line (no clean
 *      inverse for perpendicular-distance offset math).
 *
 *  Shared by every bidirectional editing surface (line drag, vertex
 *  drag, rotation knob, properties-panel rotation scrubber) so the
 *  refusal contract stays uniform. */
export function walkMirrorChainToSource(
  project: Project,
  derivedLineId: string,
  scope: paper.PaperScope,
): { sourceRootId: string; matrices: MirrorMatrix[] } | null {
  const lines = projectConstructionLines(project);
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const start = byId.get(derivedLineId);
  if (!start?.mirrorSource) return null;
  const matrices: MirrorMatrix[] = [];
  let cur: string = derivedLineId;
  for (let i = 0; i < 1000; i++) {
    const line = byId.get(cur);
    if (!line) return null;
    if (!line.mirrorSource) {
      if (isOffsetDerived(line)) return null;
      return { sourceRootId: cur, matrices };
    }
    const axisSpec = resolveAxisSpec(project, line.mirrorSource.axis, scope);
    if (!axisSpec) return null;
    matrices.push(buildMirrorMatrix(axisSpec));
    cur = line.mirrorSource.sourceId;
  }
  return null;
}

/** Map a rotation `(angle, pivot)` expressed in DERIVED space (i.e.,
 *  what the user is dialing on the mirror) into the equivalent
 *  rotation expressed in SOURCE-ROOT space. The mirror's pathData
 *  will then re-derive across each fixed axis to produce the
 *  rotation the user perceives.
 *
 *  For a segment-axis (line reflection) the conjugation
 *  `M · R(θ, c) · M` is itself a rotation `R(-θ, M(c))` — the angle
 *  sign flips because reflections are orientation-reversing, and
 *  the pivot reflects through the axis. For a point-axis
 *  (180° rotation) the conjugation is orientation-preserving so the
 *  angle survives unchanged. Both flavours reflect the pivot
 *  through the matrix.
 *
 *  Chain composition: each matrix in the chain reflects the pivot
 *  in outer-to-inner order and toggles the sign independently
 *  (point axes don't toggle).
 *
 *  Walks PAST offset hops (issue #180): offsets preserve rotation —
 *  `applySourceRotationToPath` in the offset deriver copies
 *  `source.rotation` / `source.rotationCenter` straight through —
 *  so the routing treats each offset as identity (no pivot reflect,
 *  no sign toggle). The walker continues until it reaches a truly
 *  non-derived root, so rotating a `mirror(offset(S, d), X)` line
 *  ends up rotating S and propagating forward through the offset +
 *  mirror derivers naturally. */
export function routeMirrorRotationToSource(
  project: Project,
  derivedLineId: string,
  derivedPivot: Vec2,
  scope: paper.PaperScope,
): {
  sourceRootId: string;
  sourcePivot: Vec2;
  sign: 1 | -1;
} | null {
  const lines = projectConstructionLines(project);
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const start = byId.get(derivedLineId);
  if (!start?.mirrorSource) return null;
  let p: Vec2 = { x: derivedPivot.x, y: derivedPivot.y };
  let sign: 1 | -1 = 1;
  let cur: string = derivedLineId;
  for (let i = 0; i < 1000; i++) {
    const line = byId.get(cur);
    if (!line) return null;
    if (line.mirrorSource) {
      const axisSpec = resolveAxisSpec(project, line.mirrorSource.axis, scope);
      if (!axisSpec) return null;
      const m = buildMirrorMatrix(axisSpec);
      const det = m.m11 * m.m22 - m.m12 * m.m21;
      if (det < 0) sign = (sign === 1 ? -1 : 1) as 1 | -1;
      p = applyMirror(m, p);
      cur = line.mirrorSource.sourceId;
      continue;
    }
    if (line.offsetSource) {
      // Offset is rotation-identity — the deriver propagates the
      // source's rotation forward to the offset, so routing a
      // rotation through this hop doesn't change the pivot or sign.
      cur = line.offsetSource.sourceId;
      continue;
    }
    return { sourceRootId: cur, sourcePivot: p, sign };
  }
  return null;
}

/** True when `derivedLineId` is a mirror line whose source chain
 *  resolves to a NON-OFFSET root. Drag handlers consult this
 *  before routing a mirror-derived edit through the bidirectional
 *  bridge; when false, the drag should be refused with a hint. */
export function canBidirectionallyEditMirror(
  project: Project,
  derivedLineId: string,
  scope: paper.PaperScope,
): boolean {
  // Use the same inverse-edit helper to verify the chain. Passing
  // a throw-away (0, 0) target is cheap and exercises the same
  // resolver as the real drag.
  const result = inverseMirrorVertexEdit(
    project,
    derivedLineId,
    0,
    0,
    { x: 0, y: 0 },
    scope,
  );
  return result !== null;
}
